import React, { useCallback, useEffect, useRef, useState } from "react";
import popup, { answersPopup } from "./popup";
import { SLOT } from "./desiAssets";

/**
 * Gujarat (slot/game.php + slot2.js): spin the reels, answer a True / False
 * question after each spin. Three questions end the game.
 * Scoring: 1 point per correct answer, and a one-time +5 bonus when three gold
 * coins line up in a row (any of the three visible rows) on a single spin.
 * Gold coins from different spins are not added up.
 */
const SYMBOL_IDS = ["energy-64", "staff-64", "cash-64", "build-64", "goods-64", "gold-64"];
const ITEM_COUNT = SYMBOL_IDS.length;
const CELLS = ITEM_COUNT * 2;
const GOLD = "gold-64";
const BONUS_POINTS = 5;

const SPIN_SPEED = 0.01; // reel items per ms while spinning
const RUNTIME = 1000; // all reels spin before the first one stops
const STOP_GAP = 600; // between one reel stopping and the next
const STOP_DURATION = 900; // deceleration of a reel
const SETTLE = 1000; // pause on the result before the question

/** Reel x-ranges inside main.png (816px wide): the three cream panels. */
const REEL_BOUNDS = [
  { left: (53 / 816) * 100, width: (230 / 816) * 100 },
  { left: (291 / 816) * 100, width: (255 / 816) * 100 },
  { left: (554 / 816) * 100, width: (214 / 816) * 100 },
];

const QUESTIONS = [
  {
    question:
      "In many households, women apply kajal made from the flames of the diyas to bring in some fortune and prosperity.",
    options: ["True", "False"],
    correct: "True",
  },
  {
    question: "The festival occurs on a full-moon night",
    options: ["True", "False"],
    correct: "False",
  },
  {
    question: "Diwali Celebration marks the end of the harvest season",
    options: ["True", "False"],
    correct: "True",
  },
];

function shuffled(list) {
  const out = [...list];
  for (let i = out.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

const newOrders = () => [shuffled(SYMBOL_IDS), shuffled(SYMBOL_IDS), shuffled(SYMBOL_IDS)];

/**
 * The reels show three rows: row 0 is the middle (the result), -1 the top, +1 the bottom.
 * True only when all three reels show a gold coin in the same visible row.
 */
export function hasGoldLine(orders, results) {
  const goldAt = (reel, row) => orders[reel][(results[reel] + row + ITEM_COUNT) % ITEM_COUNT] === GOLD;
  return [-1, 0, 1].some((row) => [0, 1, 2].every((reel) => goldAt(reel, row)));
}

export default function SlotGame({ api }) {
  const { setScore, submit, initialState, saveProgress, setReveal } = api;
  // Refresh: the question the player is on, their gold coins, points and answers come back from the DB.
  const saved = initialState || {};
  const [orders, setOrders] = useState(newOrders);
  const [phase, setPhase] = useState(
    saved.phase === "question" || saved.phase === "done" ? saved.phase : "idle"
  ); // idle | spinning | question | done
  const [questionIndex, setQuestionIndex] = useState(
    Math.min(Math.max(0, Math.floor(Number(saved.questionIndex)) || 0), QUESTIONS.length)
  );
  const [choice, setChoice] = useState("");

  const stripRefs = useRef([]);
  const rafRef = useRef(0);
  const ordersRef = useRef(orders);
  const goldRef = useRef(Number(saved.gold) || 0);
  const pointsRef = useRef(Number(saved.points) || 0);
  const answersRef = useRef(Array.isArray(saved.answers) ? [...saved.answers] : []);
  const questionIndexRef = useRef(questionIndex);
  const bonusRef = useRef(!!saved.bonus);
  const recapShownRef = useRef(false);

  ordersRef.current = orders;
  questionIndexRef.current = questionIndex;

  const persist = useCallback(
    (nextPhase, nextQuestionIndex) =>
      saveProgress({
        phase: nextPhase,
        questionIndex: nextQuestionIndex,
        gold: goldRef.current,
        bonus: bonusRef.current,
        points: pointsRef.current,
        answers: answersRef.current,
      }),
    [saveProgress]
  );

  const applyOffset = (reel, position) => {
    const el = stripRefs.current[reel];
    if (!el) return;
    const wrapped = ((position % ITEM_COUNT) + ITEM_COUNT) % ITEM_COUNT;
    el.style.transform = `translateY(${(-wrapped / CELLS) * 100}%)`;
  };

  // Reels rest with the middle row aligned (offset = result - 1).
  useEffect(() => {
    stripRefs.current.forEach((_, i) => applyOffset(i, 0));
  }, [orders]);

  useEffect(() => () => cancelAnimationFrame(rafRef.current), []);

  const evaluate = useCallback(
    async (results) => {
      const goldLine = hasGoldLine(ordersRef.current, results);
      goldRef.current += [0, 1, 2].filter(
        (reel) => ordersRef.current[reel][results[reel] % ITEM_COUNT] === GOLD
      ).length;

      // Bonus only for three gold coins in a row on this spin, once per game.
      let bonusText = "";
      if (!bonusRef.current && goldLine) {
        bonusRef.current = true;
        pointsRef.current += BONUS_POINTS;
        setScore(pointsRef.current);
        bonusText = "Three gold coins in a row!";
      }

      setChoice("");
      persist("question", questionIndexRef.current);
      if (bonusText) {
        await popup.fire({
          icon: "success",
          title: "Bonus!",
          text: `${bonusText} +${BONUS_POINTS} bonus points.`,
          confirmButtonText: "Continue",
          timer: 3000,
          timerProgressBar: true,
        });
      }
      setPhase("question");
    },
    [setScore, persist]
  );

  // The right answers of all three questions (Skip, and the end-of-round recap).
  const showRecap = useCallback(() => {
    const given = answersRef.current;
    const items = QUESTIONS.map((q, i) => {
      const a = given[i];
      return {
        label: `Q${i + 1}. ${q.question}`,
        answer: `Correct answer: ${q.correct}`,
        correct: !!a && a.selectedAnswer === a.correctAnswer,
        yours: a ? a.selectedAnswer : "",
      };
    });
    const right = items.filter((it) => it.correct).length;
    recapShownRef.current = true;
    return answersPopup({
      title: "Round recap",
      intro: given.length ? `You got ${right} of ${QUESTIONS.length} right.` : "",
      items,
    });
  }, []);

  // Skip shows the right answers first.
  useEffect(() => {
    setReveal(() =>
      answersPopup({
        title: "Right answers",
        items: QUESTIONS.map((q, i) => ({ label: `Q${i + 1}. ${q.question}`, answer: `Correct answer: ${q.correct}` })),
      })
    );
    return () => setReveal(null);
  }, [setReveal]);

  const spin = () => {
    if (phase !== "idle") return;
    setPhase("spinning");

    const results = [0, 1, 2].map(() => Math.floor(Math.random() * ITEM_COUNT));
    const reels = [0, 1, 2].map((i) => ({
      position: Math.random() * ITEM_COUNT,
      stopping: false,
      done: false,
      t0: 0,
      p0: 0,
      distance: 0,
      target: (results[i] - 1 + ITEM_COUNT) % ITEM_COUNT,
    }));
    const startedAt = performance.now();
    let last = startedAt;
    let allDoneAt = 0;

    const frame = (now) => {
      const dt = now - last;
      last = now;
      const elapsed = now - startedAt;

      reels.forEach((reel, i) => {
        if (reel.done) return;
        if (elapsed < RUNTIME + i * STOP_GAP) {
          reel.position = (reel.position + SPIN_SPEED * dt) % ITEM_COUNT;
        } else {
          if (!reel.stopping) {
            reel.stopping = true;
            reel.t0 = now;
            reel.p0 = reel.position % ITEM_COUNT;
            const toTarget = (reel.target - reel.p0 + ITEM_COUNT) % ITEM_COUNT;
            reel.distance = toTarget + ITEM_COUNT; // one more lap to ease out of
          }
          const u = Math.min(1, (now - reel.t0) / STOP_DURATION);
          const eased = 1 - (1 - u) ** 3;
          reel.position = reel.p0 + reel.distance * eased;
          if (u >= 1) {
            reel.position = reel.target;
            reel.done = true;
          }
        }
        applyOffset(i, reel.position);
      });

      if (reels.every((r) => r.done)) {
        if (!allDoneAt) allDoneAt = now;
        if (now - allDoneAt >= SETTLE) {
          evaluate(results);
          return;
        }
      }
      rafRef.current = requestAnimationFrame(frame);
    };
    rafRef.current = requestAnimationFrame(frame);
  };

  const submitAnswer = async () => {
    if (!choice) {
      popup.fire({
        icon: "warning",
        title: "Pick an answer",
        text: "Choose True or False to continue.",
        confirmButtonText: "Got it",
      });
      return;
    }
    const current = QUESTIONS[questionIndex];
    answersRef.current.push({
      question: current.question,
      selectedAnswer: choice,
      correctAnswer: current.correct,
    });
    if (choice === current.correct) {
      pointsRef.current += 1;
      setScore(pointsRef.current);
    }

    const next = questionIndex + 1;
    if (next >= QUESTIONS.length) {
      setPhase("done");
      persist("done", QUESTIONS.length);
      await showRecap();
      await submit(pointsRef.current, answersRef.current);
      return;
    }

    setQuestionIndex(next);
    setOrders(newOrders());
    setPhase("idle");
    persist("idle", next);
  };

  const current = QUESTIONS[Math.min(questionIndex, QUESTIONS.length - 1)];
  const spinLabel =
    phase === "spinning"
      ? "SPINNING…"
      : phase === "done"
        ? "SUBMIT SCORE"
        : questionIndex === 0
          ? "Click to Spin"
          : "SPIN AGAIN";
  const spinDisabled = (phase !== "idle" && phase !== "done") || api.saving;
  const onSpinClick = async () => {
    if (phase !== "done") {
      spin();
      return;
    }
    // Refreshed after the last answer: still show the right answers before submitting.
    if (!recapShownRef.current) await showRecap();
    submit(pointsRef.current, answersRef.current);
  };

  return (
    <>
      <div
        className="slot-machine"
        style={{ backgroundImage: `url(${SLOT.main})` }}
      >
        {orders.map((order, reel) => (
          <div
            key={reel}
            className="slot-reel"
            style={{
              left: `${REEL_BOUNDS[reel].left}%`,
              width: `${REEL_BOUNDS[reel].width}%`,
            }}
          >
            <div
              className="slot-reel__strip"
              ref={(el) => {
                stripRefs.current[reel] = el;
              }}
            >
              {Array.from({ length: CELLS }, (_, cell) => (
                <div key={cell} className="slot-reel__cell">
                  <img
                    src={SLOT.symbols[order[cell % ITEM_COUNT]]}
                    alt=""
                    draggable={false}
                  />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <button
        type="button"
        className="slot-spin"
        disabled={spinDisabled}
        onClick={onSpinClick}
      >
        {spinLabel}
      </button>
      <p className="slot-rules">
        Spin the reels, then answer each True / False question: 1 point for a
        correct answer, and 5 bonus points when 3 gold coins line up in a row.
      </p>

      {phase === "question" ? (
        <div className="slot-modal" role="dialog" aria-modal="true">
          <div className="slot-modal__card">
            <p className="slot-modal__step">
              Question {questionIndex + 1} of {QUESTIONS.length}
            </p>
            <p className="slot-modal__question">{current.question}</p>
            <div className="slot-modal__options">
              {current.options.map((option) => (
                <label key={option} className="slot-modal__option">
                  <input
                    type="radio"
                    name="slot-option"
                    value={option}
                    checked={choice === option}
                    onChange={() => setChoice(option)}
                  />
                  {option}
                </label>
              ))}
            </div>
            <button
              type="button"
              className="slot-modal__submit"
              disabled={!choice}
              onClick={submitAnswer}
            >
              Submit answer
            </button>
          </div>
        </div>
      ) : null}
    </>
  );
}
